import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO: string;
    FECHATERMINO: string;
}

interface CutHeader {
    CVECORTE_OUT: number;
    CVEGRUPO: number;
    GRUPO: string;
    OFICIAL: number;
    CVEEVENTO: number;
    CVETORNEO: number;
    HCP: number;
    NUMLINEAS: number;
    TIPO: string;
    CORTE: string;
    CUTRATIO: number;
    MINREQUIREDCUT: number;
    CUTSPEC: string;
    FINALS: number;
    CALIFICADOS: number;
    CUTFIELD: string;
}

interface PlayerResult {
    CVEJUGADOR: number;
    CVETURNO: number;
    CVEEVENTO_OUT: number;
    CVETORNEO_OUT: number;
    NOMBRECOMPLETO: string;
    NOMBREREPORTES: string;
    CALLE: string;
    ZIP: string;
    CITY: string;
    ESTADO: string;
    PAISCORTO: string;
    PAIS: string;
    RESIDENCIA: string;
    FICHANAL: string;
    DIA_I: string;
    DIA_E: string;
    FECHA: string;
    HORA: string;
    HCP: number;
    SMH: number;
    TMI: number;
    SMN: number;
    NUMLIN: number;
    CVEITEM: number;
    LEYENDA: string;
    QUAL: number;
    MONTO: number;
    PREVQUAL: number;
    ORDENPOR: number;
    PREMIOFIJO: number;
    REPARTIDO: number;
    MESA: number;
    MESAS: string;
    LINEAS: string;
}

interface CutInfo {
    CVECORTE: number;
    CVEGRUPO: number;
    CUT_NAME: string;
    GROUP_NAME: string;
    CVEEVENTO: number;
    CVETORNEO: number;
}

const AllTournamentResults: React.FC = () => {
    const navigate = useNavigate();
    
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingResults, setLoadingResults] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const [allCuts, setAllCuts] = useState<CutInfo[]>([]);
    const [cutCompletionStatus, setCutCompletionStatus] = useState<{ [key: string]: any }>({});
    const [selectedCut, setSelectedCut] = useState<CutInfo | null>(null);
    
    const [header, setHeader] = useState<CutHeader | null>(null);
    const [results, setResults] = useState<PlayerResult[]>([]);

    // Load all tournaments on mount
    useEffect(() => {
        loadTournaments();
    }, []);

    // Load cuts when tournament is selected
    useEffect(() => {
        if (selectedTournamentId) {
            loadTournamentCuts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTournamentId]);

    // Load results when cut is selected
    useEffect(() => {
        if (selectedCut) {
            loadResults();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCut]);

    const loadTournaments = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${BACKEND_URL}/tournaments/all-for-results`);
            if (!response.ok) {
                throw new Error('Failed to load tournaments');
            }
            const data = await response.json();
            setTournaments(data);
        } catch (err: any) {
            console.error('Error loading tournaments:', err);
            setError(err.message || 'Failed to load tournaments');
        } finally {
            setLoading(false);
        }
    };

    const loadTournamentCuts = async () => {
        if (!selectedTournamentId) return;

        setLoadingResults(true);
        setError(null);
        setAllCuts([]);
        setSelectedCut(null);
        setResults([]);
        setHeader(null);

        try {
            // Get all events for this tournament (including qualifying and finals)
            const response = await fetch(`${BACKEND_URL}/tournaments/all-cuts/${selectedTournamentId}`);
            
            if (!response.ok) {
                throw new Error('Failed to load tournament results');
            }

            const cutsData = await response.json();
            
            // Check completion status for each cut
            const completionStatus: { [key: string]: any } = {};
            await Promise.all(
                cutsData.map(async (cut: CutInfo) => {
                    try {
                        const completionResponse = await fetch(
                            `${BACKEND_URL}/api/my-tournaments/cut-complete/${cut.CVECORTE}/${cut.CVEGRUPO}`
                        );
                        const completionData = await completionResponse.json();
                        const key = `${cut.CVECORTE}-${cut.CVEGRUPO}`;
                        completionStatus[key] = completionData;
                    } catch (err) {
                        console.error(`Error checking completion for cut ${cut.CVECORTE}-${cut.CVEGRUPO}:`, err);
                    }
                })
            );
            
            setCutCompletionStatus(completionStatus);
            setAllCuts(cutsData);

            // Set the initial selected cut to the first complete one
            const firstCompleteCut = cutsData.find((c: CutInfo) => {
                const key = `${c.CVECORTE}-${c.CVEGRUPO}`;
                return completionStatus[key]?.complete;
            }) || cutsData[0];

            setSelectedCut(firstCompleteCut);
        } catch (err: any) {
            console.error('Error loading tournament cuts:', err);
            setError(err.message || 'Failed to load tournament results');
        } finally {
            setLoadingResults(false);
        }
    };

    const loadResults = async () => {
        if (!selectedCut) return;

        setLoadingResults(true);
        setError(null);

        try {
            const response = await fetch(
                `${BACKEND_URL}/api/my-tournaments/cut-results/${selectedCut.CVECORTE}/${selectedCut.CVEGRUPO}/${selectedCut.CVETORNEO}/${selectedCut.CVEEVENTO}`
            );

            if (!response.ok) {
                throw new Error('Failed to load results');
            }

            const data = await response.json();
            setHeader(data.header);
            setResults(data.results);
        } catch (err: any) {
            console.error('Error loading results:', err);
            setError(err.message || 'Failed to load results');
        } finally {
            setLoadingResults(false);
        }
    };

    const handleTournamentSelect = (tournamentId: number) => {
        setSelectedTournamentId(tournamentId);
    };

    const calculateCutLine = () => {
        if (!header || !results.length) return null;

        const totalPlayers = results.length;
        let cutLinePosition = null;

        if (header.CUTRATIO && header.CUTRATIO > 0) {
            cutLinePosition = Math.ceil(totalPlayers * header.CUTRATIO);
        } else if (header.MINREQUIREDCUT) {
            cutLinePosition = header.MINREQUIREDCUT;
        }

        if (cutLinePosition && cutLinePosition < totalPlayers) {
            return cutLinePosition;
        }

        return null;
    };

    const formatScore = (value: number | null | undefined) => {
        if (value === null || value === undefined) return '-';
        return Math.round(value).toLocaleString();
    };

    const cutLinePosition = calculateCutLine();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-red-600 text-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex-shrink-0 bg-white bg-opacity-20 hover:bg-opacity-30 p-3 rounded-xl transition-all hover:scale-110"
                            aria-label="Go back"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-2xl sm:text-3xl font-bold mb-1 flex items-center gap-3">
                                <svg className="w-8 h-8 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                                <span className="truncate">All Tournament Results</span>
                            </h1>
                            {selectedCut && (
                                <p className="text-blue-100 text-lg font-medium truncate">
                                    {selectedCut.CUT_NAME} - {selectedCut.GROUP_NAME}
                                    {header && (`:`)}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Tournament Selector */}
                    <div className="mt-4">
                        <label className="block text-white text-sm font-semibold mb-2">
                            Select Tournament
                        </label>
                        <select
                            value={selectedTournamentId || ''}
                            onChange={(e) => handleTournamentSelect(Number(e.target.value))}
                            className="w-full px-4 py-3 rounded-xl font-semibold shadow-lg border-2 border-white bg-white text-gray-900 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all"
                            disabled={loading}
                        >
                            <option value="">-- Select a Tournament --</option>
                            {tournaments.map((tournament) => (
                                <option key={tournament.CLAVE} value={tournament.CLAVE}>
                                    {tournament.NOMBRE}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Division Selectors */}
                    {allCuts.length > 1 && selectedCut && selectedTournamentId && (() => {
                        // Group cuts by division (GROUP_NAME) and then by event (CUT_NAME)
                        const divisionGroups = allCuts.reduce((acc: any, cut) => {
                            const divisionKey = cut.GROUP_NAME;
                            if (!acc[divisionKey]) {
                                acc[divisionKey] = [];
                            }
                            acc[divisionKey].push(cut);
                            return acc;
                        }, {});

                        return (
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {Object.keys(divisionGroups).map((divisionName) => {
                                    const cuts = divisionGroups[divisionName];
                                    
                                    // If only one cut in this division, show as single button
                                    if (cuts.length === 1) {
                                        const cut = cuts[0];
                                        const key = `${cut.CVECORTE}-${cut.CVEGRUPO}`;
                                        const status = cutCompletionStatus[key];
                                        const isComplete = status?.complete;
                                        const isSelected = selectedCut?.CVECORTE === cut.CVECORTE && selectedCut?.CVEGRUPO === cut.CVEGRUPO;
                                        
                                        return (
                                            <button
                                                key={divisionName}
                                                onClick={() => {
                                                    setSelectedCut(cut);
                                                }}
                                                className={`w-full px-4 py-3 rounded-xl font-bold shadow-lg border-2 transition-all ${
                                                    isSelected
                                                        ? 'bg-white text-blue-600 border-white'
                                                        : isComplete
                                                        ? 'bg-white bg-opacity-20 text-white border-white border-opacity-20 hover:bg-opacity-30 cursor-pointer'
                                                        : 'bg-white bg-opacity-10 text-white border-white border-opacity-20 hover:bg-opacity-20 cursor-pointer'
                                                }`}
                                            >
                                                <div className="text-center">
                                                    <div className="text-base font-bold">{divisionName}</div>
                                                    <div className="text-sm opacity-90 truncate">{cut.CUT_NAME}</div>
                                                    {!isComplete && status && (
                                                        <div className="text-xs mt-1">({status.bowledSquads}/{status.totalSquads} squads)</div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    }
                                    
                                    // Multiple cuts in division - show as dropdown
                                    const selectedInThisDivision = cuts.find((c: any) => 
                                        c.CVECORTE === selectedCut?.CVECORTE && c.CVEGRUPO === selectedCut?.CVEGRUPO
                                    );
                                    
                                    // Default to first cut in division (complete or not)
                                    const defaultCut = cuts[0];
                                    const dropdownValue = selectedInThisDivision 
                                        ? `${selectedInThisDivision.CVECORTE}-${selectedInThisDivision.CVEGRUPO}`
                                        : defaultCut
                                        ? `${defaultCut.CVECORTE}-${defaultCut.CVEGRUPO}`
                                        : '';
                                    
                                    return (
                                        <div key={divisionName} className="flex flex-col gap-1">
                                            <label className="text-white text-xs font-semibold px-1">
                                                {divisionName}
                                            </label>
                                            <select
                                                value={dropdownValue}
                                                onChange={(e) => {
                                                    const [cvecorte, cvegrupo] = e.target.value.split('-').map(Number);
                                                    const cut = allCuts.find(c => c.CVECORTE === cvecorte && c.CVEGRUPO === cvegrupo);
                                                    if (cut) {
                                                        setSelectedCut(cut);
                                                    }
                                                }}
                                                className={`w-full px-4 py-2 rounded-xl font-semibold shadow-lg border-2 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all cursor-pointer ${
                                                    selectedInThisDivision 
                                                        ? 'bg-white text-blue-600 border-white' 
                                                        : 'bg-white bg-opacity-20 text-white border-white border-opacity-20 hover:bg-opacity-30'
                                                }`}
                                            >
                                                {cuts.map((cut: any) => {
                                                    const key = `${cut.CVECORTE}-${cut.CVEGRUPO}`;
                                                    const status = cutCompletionStatus[key];
                                                    const isComplete = status?.complete;
                                                    const squadInfo = status ? ` (${status.bowledSquads}/${status.totalSquads} squads)` : '';
                                                    
                                                    return (
                                                        <option
                                                            key={key}
                                                            value={key}
                                                            className="text-gray-900"
                                                        >
                                                            {cut.CUT_NAME}{!isComplete && ` - In Progress${squadInfo}`}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 text-lg">Loading tournaments...</p>
                        </div>
                    </div>
                ) : !selectedTournamentId ? (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select a Tournament</h2>
                        <p className="text-gray-600">Choose a tournament from the dropdown above to view its results</p>
                    </div>
                ) : (
                    <>
                        {/* Incomplete Cut Warning */}
                        {selectedCut && (() => {
                            const key = `${selectedCut.CVECORTE}-${selectedCut.CVEGRUPO}`;
                            const status = cutCompletionStatus[key];
                            if (status && !status.complete) {
                                return (
                                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-lg mb-4 shadow-md">
                                        <div className="flex items-center gap-3">
                                            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            <div>
                                                <p className="font-bold">This cut is not complete yet</p>
                                                <p className="text-sm">Only {status.bowledSquads} of {status.totalSquads} squads have been bowled. Results shown are partial.</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <div 
                            key={selectedCut ? `${selectedCut.CVECORTE}-${selectedCut.CVEGRUPO}` : 'no-selection'}
                            className="bg-white rounded-2xl shadow-xl overflow-hidden"
                        >
                            {loadingResults ? (
                                <div className="flex items-center justify-center py-20">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                                        <p className="text-gray-600 text-lg">Loading results...</p>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="text-center py-20">
                                    <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-red-600 text-lg font-semibold mb-2">Error Loading Results</p>
                                    <p className="text-gray-600">{error}</p>
                                </div>
                            ) : results.length === 0 ? (
                                <div className="text-center py-20">
                                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                    <p className="text-gray-600 text-lg">No results available yet</p>
                                </div>
                            ) : (
                                <>
                                    {/* Mobile Card View */}
                                    <div className="block md:hidden divide-y divide-gray-200">
                                        {results.map((player, index) => {
                                            const position = index + 1;
                                            const isQualified = player.QUAL === 1;
                                            const isCutLine = cutLinePosition && position === cutLinePosition;
                                            const isBelowCut = cutLinePosition && position > cutLinePosition;
                                            const average = player.NUMLIN > 0 ? Math.round(player.SMN / player.NUMLIN) : 0;

                                            return (
                                                <React.Fragment key={`${player.CVEJUGADOR}-${player.CVETURNO}`}>
                                                    {isCutLine && (
                                                        <div className="relative h-8 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 flex items-center justify-center">
                                                            <span className="bg-yellow-600 text-white px-4 py-1 text-xs font-bold rounded-full shadow-lg">
                                                                CUT LINE
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className={`p-4 ${isQualified ? 'bg-green-50' : 'bg-white'} ${isBelowCut ? 'opacity-60' : ''}`}>
                                                        <div className="flex gap-4">
                                                            {/* Position Badge */}
                                                            <div className="flex-shrink-0">
                                                                <div className={`w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg shadow-lg ${
                                                                    position <= 3
                                                                        ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white'
                                                                        : isQualified
                                                                        ? 'bg-green-600 text-white'
                                                                        : isBelowCut
                                                                        ? 'bg-gray-400 text-white'
                                                                        : 'bg-blue-600 text-white'
                                                                }`}>
                                                                    {position}
                                                                </div>
                                                            </div>

                                                            {/* Player Info */}
                                                            <div className="flex-1 min-w-0">
                                                                {/* Name and Location */}
                                                                <div className="mb-3">
                                                                    <h3 className="font-bold text-gray-900 text-lg leading-tight">
                                                                        {player.NOMBREREPORTES}
                                                                    </h3>
                                                                    <p className="text-sm text-gray-600">{player.RESIDENCIA}</p>
                                                                </div>

                                                                {/* Scores Grid */}
                                                                {(() => { const isHighGame = header?.CUTSPEC === 'H'; return (
                                                                <div className={`grid gap-3 mb-3 ${isHighGame ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                                                    <div className="bg-blue-50 rounded-lg p-2">
                                                                        <p className="text-xs text-gray-600 font-medium">{isHighGame ? 'High Game' : 'Total Score'}</p>
                                                                        <p className="text-2xl font-bold text-blue-600">{formatScore(player.ORDENPOR)}</p>
                                                                    </div>
                                                                    {!isHighGame && (
                                                                    <div className="bg-gray-50 rounded-lg p-2">
                                                                        <p className="text-xs text-gray-600 font-medium">Scratch</p>
                                                                        <p className="text-2xl font-bold text-gray-700">{formatScore(player.SMN)}</p>
                                                                    </div>
                                                                    )}
                                                                </div>
                                                                ); })()}

                                                                {/* Details Grid */}
                                                                <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                                                                    <div>
                                                                        <p className="text-xs text-gray-500">HCP</p>
                                                                        <p className="font-semibold text-gray-700">{player.HCP || 0}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs text-gray-500">Games</p>
                                                                        <p className="font-semibold text-gray-700">{player.NUMLIN}</p>
                                                                    </div>
                                                                    {header?.TIPO === 'S' && (
                                                                        <div>
                                                                            <p className="text-xs text-gray-500">Average</p>
                                                                            <p className="font-semibold text-gray-700">{average}</p>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Squad Info */}
                                                                <div className="text-sm mb-3 bg-gray-50 rounded-lg p-2">
                                                                    <p className="text-xs text-gray-500">Squad</p>
                                                                    <p className="font-semibold text-gray-700">{player.DIA_I} - {player.HORA}</p>
                                                                </div>

                                                                {/* Status Badges */}
                                                                <div className="flex flex-wrap gap-2">
                                                                    {isQualified && (
                                                                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">
                                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                            </svg>
                                                                            Qualified
                                                                        </span>
                                                                    )}
                                                                    {player.LEYENDA && (
                                                                        <span className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                                                                            {player.LEYENDA}
                                                                        </span>
                                                                    )}
                                                                    {player.MONTO > 0 && (
                                                                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-bold">
                                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                                                            </svg>
                                                                            ${player.MONTO.toFixed(2)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>

                                    {/* Desktop Table View */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="bg-gray-100 border-b-2 border-gray-300">
                                                    <th className="px-4 py-3 text-left font-bold text-gray-700">Pos</th>
                                                    <th className="px-4 py-3 text-left font-bold text-gray-700">Player</th>
                                                    <th className="px-4 py-3 text-center font-bold text-gray-700">Squad</th>
                                                    <th className="px-4 py-3 text-right font-bold text-gray-700">{header?.CUTSPEC === 'H' ? 'High Game' : 'Total'}</th>
                                                    {header?.CUTSPEC !== 'H' && <th className="px-4 py-3 text-right font-bold text-gray-700">Scratch</th>}
                                                    <th className="px-4 py-3 text-center font-bold text-gray-700">HCP</th>
                                                    <th className="px-4 py-3 text-center font-bold text-gray-700">Games</th>
                                                    {header?.TIPO === 'S' && (
                                                        <th className="px-4 py-3 text-right font-bold text-gray-700">Avg</th>
                                                    )}
                                                    <th className="px-4 py-3 text-center font-bold text-gray-700">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.map((player, index) => {
                                                    const position = index + 1;
                                                    const isQualified = player.QUAL === 1;
                                                    const isCutLine = cutLinePosition && position === cutLinePosition;
                                                    const isBelowCut = cutLinePosition && position > cutLinePosition;
                                                    const average = player.NUMLIN > 0 ? Math.round(player.SMN / player.NUMLIN) : 0;

                                                    return (
                                                        <React.Fragment key={`${player.CVEJUGADOR}-${player.CVETURNO}`}>
                                                            {isCutLine && (
                                                                <tr>
                                                                    <td colSpan={header?.CUTSPEC === 'H' ? (header?.TIPO === 'S' ? 8 : 7) : (header?.TIPO === 'S' ? 9 : 8)} className="p-0">
                                                                        <div className="relative h-8 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 flex items-center justify-center">
                                                                            <span className="bg-yellow-600 text-white px-4 py-1 text-xs font-bold rounded-full shadow-lg">
                                                                                CUT LINE
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            <tr className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                                                                isQualified ? 'bg-green-50' : ''
                                                            } ${isBelowCut ? 'opacity-60' : ''}`}>
                                                                <td className="px-4 py-3 text-left whitespace-nowrap">
                                                                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                                                                        position <= 3
                                                                            ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white shadow-lg'
                                                                            : isQualified
                                                                            ? 'bg-green-600 text-white'
                                                                            : isBelowCut
                                                                            ? 'bg-gray-400 text-white'
                                                                            : 'bg-blue-600 text-white'
                                                                    }`}>
                                                                        {position}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <div>
                                                                        <p className="font-bold text-gray-900 whitespace-nowrap">{player.NOMBREREPORTES}</p>
                                                                        <p className="text-sm text-gray-600 whitespace-nowrap">{player.RESIDENCIA}</p>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <div className="text-sm">
                                                                        <p className="text-gray-600 whitespace-nowrap">{player.DIA_I}</p>
                                                                        <p className="text-gray-500 whitespace-nowrap">{player.HORA}</p>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-right">
                                                                    <p className="font-bold text-blue-600 text-lg whitespace-nowrap">{formatScore(player.ORDENPOR)}</p>
                                                                </td>
                                                                {header?.CUTSPEC !== 'H' && (
                                                                <td className="px-4 py-3 text-right">
                                                                    <p className="font-semibold text-gray-700 whitespace-nowrap">{formatScore(player.SMN)}</p>
                                                                </td>
                                                                )}
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className="text-gray-600 font-medium">{player.HCP || 0}</span>
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className="text-gray-600 font-medium">{player.NUMLIN}</span>
                                                                </td>
                                                                {header?.TIPO === 'S' && (
                                                                    <td className="px-4 py-3 text-right">
                                                                        <span className="text-gray-600 font-medium">{average}</span>
                                                                    </td>
                                                                )}
                                                                <td className="px-4 py-3">
                                                                    <div className="flex flex-col gap-1">
                                                                        {isQualified && (
                                                                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-lg text-xs font-bold whitespace-nowrap">
                                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                                </svg>
                                                                                Qualified
                                                                            </span>
                                                                        )}
                                                                        {player.LEYENDA && (
                                                                            <span className="text-xs text-gray-600 italic whitespace-nowrap">{player.LEYENDA}</span>
                                                                        )}
                                                                        {player.MONTO > 0 && (
                                                                            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-lg text-xs font-bold whitespace-nowrap">
                                                                                ${player.MONTO.toFixed(2)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* Footer */}
                            {!loadingResults && !error && results.length > 0 && (
                                <div className="bg-gray-50 border-t-2 border-gray-200 p-4">
                                    <div className="flex flex-wrap items-center justify-between gap-4">
                                        <div className="flex items-center gap-6 text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 bg-green-50 border-2 border-green-600 rounded"></div>
                                                <span className="text-gray-700 font-medium">Qualified</span>
                                            </div>
                                            {cutLinePosition && (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                                                    <span className="text-gray-700 font-medium">Cut Line</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-700 font-semibold">Total Players: {results.length}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AllTournamentResults;
